import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api, DEMO, setApiRegion } from './api';
import { defaultSheet, ROLES } from './datasheet';
import { canUser } from './perms';
import { LPA_LOGO_B64 } from './badges';
import { RegionContext, REGIONS, REGION_STORAGE_KEY } from './region';
import Login from './components/Login';
import ForcePassword from './components/ForcePassword';
import SKUsView from './components/SKUsView';
import ProductSearchView from './components/ProductSearchView';
import DashboardView from './components/DashboardView';
import SalesIntelPage from './components/SalesIntelPage';
import LibraryView from './components/LibraryView';
import MigrationWorkbench from './components/MigrationWorkbench';
import AdminView from './components/AdminView';
import HelpView from './components/HelpView';
import Projects from './components/Projects';
import ProposalStudioTab from './components/ProposalStudioTab';
import CodeGenerator from './components/CodeGenerator';
import AiImportView from './components/AiImportView';
import ArchiveView from './components/ArchiveView';
import NetSuiteView from './components/NetSuiteView';
import NetSuiteConnect from './components/NetSuiteConnect';
import PricingCalc from './components/PricingCalc';
import QuotesView from './components/QuotesView';
import CoreRangeView from './components/CoreRangeView';
import ApprovalsView from './components/ApprovalsView';

const LPA_LOGO = 'data:image/png;base64,' + LPA_LOGO_B64;

function AppHeader({ user, region, canSwitchRegion, toggleRegion, showCodegen, onCodegen, onSignOut }) {
  const role = user.role;
  return (
    <header className="top-bar">
      <div className="brand"><img className="brand-logo" src={LPA_LOGO} alt="LPA" /><span>Datasheets</span>
        <span data-testid="region-badge" className={'region-pill region-' + region}>{REGIONS[region].label}</span>
      </div>
      <div className="top-actions">
        {canSwitchRegion && (
          <button className={'btn region-toggle' + (region === 'US' ? ' on' : '')} data-testid="region-toggle" onClick={toggleRegion} title="Switch site region (AU / US)">
            {region === 'US' ? 'US Mode' : 'AU Mode'}
          </button>
        )}
        {showCodegen && <button className="btn" onClick={onCodegen} title="LPA product code generator">Product Code</button>}
        <div className="user-chip">{user.name || user.email}<span className={'role-pill role-' + role}>{ROLES[role] || role}</span>
          <button className="btn btn-sm" onClick={onSignOut} style={{ padding: '2px 8px' }}>Sign out</button></div>
      </div>
    </header>
  );
}

function NavRibbon({ tabs, safeView, setView }) {
  const [openGroup, setOpenGroup] = React.useState(null);
  useEffect(() => {
    const close = () => setOpenGroup(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);
  // Standalone tabs render directly; grouped tabs collapse into dropdowns (click or hover).
  const rendered = new Set();
  const nodes = [];
  tabs.forEach(t => {
    if (!t.group) {
      nodes.push(
        <a key={t.id} href={VIEW_PATHS[t.id] || '/'} data-testid={'nav-' + t.id}
          className={'nav-tab' + (safeView === t.id ? ' active' : '')}
          onClick={(e) => { e.preventDefault(); setView(t.id); }}>{t.label}</a>
      );
      return;
    }
    if (rendered.has(t.group)) return;
    rendered.add(t.group);
    const children = tabs.filter(x => x.group === t.group);
    const activeChild = children.find(c => c.id === safeView);
    nodes.push(
      <div key={'grp-' + t.group}
        className={'nav-group' + (activeChild ? ' active' : '') + (openGroup === t.group ? ' open' : '')}
        data-testid={'nav-group-' + t.group.toLowerCase()}>
        <button type="button" className="nav-tab nav-group-label"
          onClick={(e) => { e.stopPropagation(); setOpenGroup(g => g === t.group ? null : t.group); }}>
          {t.group}{activeChild && activeChild.label !== t.group ? ' · ' + activeChild.label : ''} <span className="nav-caret">▾</span>
        </button>
        <div className="nav-menu">
          {children.map(c => (
            <a key={c.id} href={VIEW_PATHS[c.id] || '/'} data-testid={'nav-' + c.id}
              className={'nav-menu-item' + (safeView === c.id ? ' active' : '')}
              onClick={(e) => { e.preventDefault(); setOpenGroup(null); setView(c.id); }}>{c.label}</a>
          ))}
        </div>
      </div>
    );
  });
  return <nav className="nav-ribbon" data-testid="nav-ribbon">{nodes}</nav>;
}

// URL path per section (e.g. lpadatasheets.com/proposals).
const VIEW_PATHS = {
  dashboard: '/', search: '/search',
  editor: '/editor', migrate: '/migrate', projects: '/projects', studio: '/proposals',
  corerange: '/core-range', approvals: '/approvals',
  codegen: '/code-generator', aiimport: '/ai-import', archive: '/archive',
  netsuite: '/netsuite', pricing: '/pricing', quotes: '/quotes', skus: '/skus', admin: '/admin', help: '/how-to',
  salesintel: '/salesintel',
};
const PATH_VIEWS = Object.fromEntries(Object.entries(VIEW_PATHS).map(([v, p]) => [p, v]));
PATH_VIEWS['/editor'] = 'editor';
const viewFromPath = () => {
  const p = window.location.pathname.replace(/\/+$/, '') || '/';
  return PATH_VIEWS[p] || 'dashboard';
};

export default function App() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [view, setViewState] = useState(viewFromPath);
  const setView = (v) => {
    setViewState(v);
    const path = VIEW_PATHS[v] || '/';
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
  };
  useEffect(() => {
    const onPop = () => setViewState(viewFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [sheet, setSheet] = useState(defaultSheet());
  const [currentId, setCurrentId] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [studioReq, setStudioReq] = useState(null);
  const [quoteReq, setQuoteReq] = useState(null);
  const [cartReq, setCartReq] = useState(null);
  const [pricingOpened, setPricingOpened] = useState(false);
  useEffect(() => { if (view === 'pricing') setPricingOpened(true); }, [view]);
  // Region preference (only honoured for GLOBAL users). AU/US users are locked
  // to their assigned region.
  const [regionPref, setRegionPref] = useState(() => {
    const r = (typeof localStorage !== 'undefined' && localStorage.getItem(REGION_STORAGE_KEY)) || 'AU';
    return r === 'US' ? 'US' : 'AU';
  });

  const userRegion = user?.region || 'AU';           // AU | US | GLOBAL
  const canSwitchRegion = userRegion === 'GLOBAL';
  const region = canSwitchRegion ? regionPref : userRegion;

  // Set synchronously during render so child fetch effects (which run before
  // parent effects) always send the correct X-Region header.
  setApiRegion(region);

  const toggleRegion = useCallback(() => {
    setRegionPref(prev => {
      const next = prev === 'US' ? 'AU' : 'US';
      try { localStorage.setItem(REGION_STORAGE_KEY, next); } catch (e) { console.warn('Could not persist region preference', e); }
      return next;
    });
    // Region data doesn't cross over — start fresh so a sheet from the other
    // region isn't left open in the editor.
    setSheet(defaultSheet());
    setCurrentId(null);
  }, []);

  useEffect(() => {
    (async () => {
      try { const { user } = await api.me(); setUser(user); }
      catch (e) { if (e?.response?.status !== 401) console.error('Session check failed', e); }
      setReady(true);
    })();
  }, []);

  const toast = useCallback((msg, err) => { setToastMsg({ msg, err }); setTimeout(() => setToastMsg(null), 3000); }, []);

  const role = user?.role;
  const isMaster = role === 'master_admin';
  const isAdmin = canUser(user, 'admin');
  const p = (perm) => canUser(user, perm);

  const tabs = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', show: p('dashboard') },
    { id: 'salesintel', label: 'Sales Intelligence', show: p('salesintel.any') },
    { id: 'search', label: 'Product Search', show: p('search') },
    { id: 'editor', label: 'Editor', show: p('datasheets.view'), group: 'Datasheets' },
    { id: 'corerange', label: 'Core Range', show: p('datasheets.corerange'), group: 'Datasheets' },
    { id: 'approvals', label: 'Approvals', show: p('datasheets.edit_library') || p('datasheets.submit'), group: 'Datasheets' },
    { id: 'migrate', label: 'Migrate', show: p('migrate'), group: 'Datasheets' },
    { id: 'codegen', label: 'Code Generator', show: p('codegen') && region !== 'US', group: 'Datasheets' },
    { id: 'aiimport', label: 'AI Import', show: p('aiimport'), group: 'Datasheets' },
    { id: 'archive', label: 'Archive', show: p('archive.view'), group: 'Datasheets' },
    { id: 'projects', label: 'Projects', show: p('projects.view'), group: 'Sales' },
    { id: 'studio', label: 'Proposal Studio', show: p('studio'), group: 'Sales' },
    { id: 'pricing', label: 'Quote Builder', show: p('quotebuilder'), group: 'Sales' },
    { id: 'quotes', label: 'Quotes', show: p('quotes.view'), group: 'Sales' },
    { id: 'netsuite', label: 'NetSuite', show: isMaster, group: 'Admin' },
    { id: 'skus', label: "SKU's", show: p('skus'), group: 'Admin' },
    { id: 'admin', label: 'Admin', show: p('admin'), group: 'Admin' },
    { id: 'help', label: 'How-To', show: true },
  ].filter(t => t.show), [role, region]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return <div style={{ padding: 40 }}>Loading…</div>;
  if (!user) return <Login onLogin={setUser} />;
  if (user.must_change_password) return <ForcePassword user={user} onDone={setUser} />;

  function signOut() { api.logout(); setUser(null); }

  const safeView = tabs.some(t => t.id === view) ? view : (tabs[0] ? tabs[0].id : 'help');

  const openProductInEditor = async (id) => {
    if (!id) { setSheet(defaultSheet()); setCurrentId(null); setView('editor'); return; }
    try {
      const p = await api.getProduct(id);
      setSheet({ ...defaultSheet(), ...(p.data || {}), family: p.family || '' });
      setCurrentId(p.id); setView('editor');
    } catch (e) { toast('Could not open the datasheet', true); }
  };

  return (
    <RegionContext.Provider value={region}>
    <div className="app-shell">
      <AppHeader user={user} region={region} canSwitchRegion={canSwitchRegion} toggleRegion={toggleRegion}
        showCodegen={p('codegen') && region !== 'US'} onCodegen={() => setView('codegen')} onSignOut={signOut} />
      <NavRibbon tabs={tabs} safeView={safeView} setView={setView} />

      <div className="workspace" key={region}>
        {safeView === 'dashboard' && <DashboardView me={user} canFlags={p('dashboard.discrepancies')} toast={toast}
          executive={role === 'executive'}
          goView={(v, fb) => setView(tabs.some(t => t.id === v) ? v : (fb || tabs[0].id))} />}
        {safeView === 'search' && <ProductSearchView toast={toast} isCreator={p('datasheets.view')} isAdmin={p('quotebuilder')} me={user}
          onOpenProduct={(p) => { setSheet({ ...defaultSheet(), ...(p.data || {}), family: p.family || '' }); setCurrentId(p.id); setView('editor'); }}
          onCreateFrom={(item) => { setSheet({ ...defaultSheet(), name: item.displayname || item.itemid, code: item.itemid }); setCurrentId(null); setView('editor'); }}
          onCreateQuote={(payload) => { setCartReq({ ...payload, n: Date.now() }); setView('pricing'); }} />}
        {safeView === 'editor' && <LibraryView sheet={sheet} setSheet={setSheet} currentId={currentId} setCurrentId={setCurrentId}
          canEditLibrary={p('datasheets.publish')} canEditPersonal={p('datasheets.personal')} canSubmitLibrary={p('datasheets.submit')} canUsCodegen={p('us.codegen')} isMaster={isMaster} toast={toast} />}
        {safeView === 'corerange' && <CoreRangeView toast={toast} canManage={p('datasheets.edit_library')} openInEditor={openProductInEditor} />}
        {safeView === 'approvals' && <ApprovalsView toast={toast} canReview={p('datasheets.edit_library')} openInEditor={openProductInEditor} />}
        {safeView === 'migrate' && <MigrationWorkbench sheet={sheet} setSheet={setSheet} currentId={currentId} setCurrentId={setCurrentId} toast={toast} />}
        {safeView === 'projects' && <Projects toast={toast} me={user} canAll={p('projects.all')} canAssign={p('projects.assign')} goStudio={(id) => { setStudioReq({ id, n: Date.now() }); setView('studio'); }} />}
        {safeView === 'studio' && <ProposalStudioTab key={studioReq ? studioReq.id + '-' + studioReq.n : 'tab'} toast={toast} isAdmin={isAdmin} initialProjectId={studioReq && studioReq.id} />}
        {safeView === 'codegen' && <CodeGenerator sheet={sheet} setSheet={setSheet} toast={toast} goEditor={() => setView('editor')} />}
        {safeView === 'aiimport' && <AiImportView toast={toast} onOpenInEditor={(s) => { setSheet(s); setCurrentId(null); setView('editor'); }} />}
        {safeView === 'archive' && <ArchiveView toast={toast} canManage={p('archive.manage')} />}
        {safeView === 'netsuite' && <NetSuiteView toast={toast} />}
        {/* Quote builder stays mounted once opened so a half-built quote survives
            clicking to another tab and back. */}
        {(safeView === 'pricing' || pricingOpened) && (
          <div className="view-keepalive" style={safeView === 'pricing' ? undefined : { display: 'none' }}>
            <PricingCalc canPriceUpdate={p('quotebuilder.priceupdate')} toast={toast} initialQuote={quoteReq} initialCart={cartReq} />
          </div>
        )}
        {safeView === 'quotes' && <QuotesView toast={toast} canAll={p('quotes.all')} canEdit={p('quotebuilder')} onEditQuote={(q) => { setQuoteReq({ q, n: Date.now() }); setView('pricing'); }} />}
        {safeView === 'salesintel' && <SalesIntelPage toast={toast} perms={{
          overall: p('salesintel.overall'), forecast: p('salesintel.forecast'),
          stale: p('salesintel.stale'), items: p('salesintel.items'), deep: p('salesintel.deep'),
        }} />}
        {safeView === 'skus' && <SKUsView toast={toast} />}
        {safeView === 'admin' && <AdminView toast={toast} isMaster={isMaster} />}
        {safeView === 'help' && <HelpView />}
      </div>

      {toastMsg && <div className={'toast show' + (toastMsg.err ? ' err' : '')}>{toastMsg.msg}</div>}
      {/* Every role signs into NetSuite so their actions are attributed to them.
          US datasheet creators never touch NetSuite, so they are exempt. */}
      {role !== 'us_datasheet_creator' && <NetSuiteConnect toast={toast} />}
      {DEMO && <div style={{ position: 'fixed', bottom: 8, left: 8, fontSize: 11, color: 'var(--text-muted)' }}>demo mode (no backend)</div>}
    </div>
    </RegionContext.Provider>
  );
}
